import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeLines } from "./Reveal";
import { IMAGES } from "../../lib/content";
import { scrollToSection } from "../../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Subtle parallax: background drifts up, foreground text drifts slower.
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  const go = () => {
    scrollToSection("#enquire");
  };

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Parallax + cinematic Ken Burns background (video-like living motion) */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 will-change-transform"
      >
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.12 }}
          animate={{ scale: [1.12, 1.22, 1.12], x: ["0%", "-2.5%", "0%"], y: ["0%", "-1.5%", "0%"] }}
          transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
        >
          <picture>
            <source media="(max-width: 639px)" srcSet={IMAGES.heroMobile} />
            <img
              src={IMAGES.heroBalcony}
              alt="Premium 3D view of a luxury Sarrgosa studio apartment opening to the golden Kasauli hills at dusk"
              fetchpriority="high"
              className="h-full w-full object-cover object-center"
            />
          </picture>
        </motion.div>
      </motion.div>

      {/* Drifting mist layers */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none z-10 mix-blend-screen opacity-40"
        style={{
          background:
            "radial-gradient(60% 40% at 70% 65%, rgba(255,244,214,0.35), transparent 60%)",
        }}
        animate={{ x: ["-4%", "6%", "-4%"], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-1/2 pointer-events-none z-10"
        style={{
          background:
            "linear-gradient(0deg, rgba(200,210,220,0.18), transparent)",
        }}
        animate={{ x: ["3%", "-5%", "3%"], opacity: [0.25, 0.45, 0.25] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Sun bloom pulse near horizon */}
      <motion.div
        aria-hidden
        className="absolute z-10 pointer-events-none rounded-full blur-3xl"
        style={{
          right: "12%",
          top: "34%",
          width: 340,
          height: 340,
          background:
            "radial-gradient(circle, rgba(255,210,130,0.55), transparent 65%)",
        }}
        animate={{ opacity: [0.35, 0.7, 0.35], scale: [0.9, 1.1, 0.9] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Floating birds */}
      <div aria-hidden className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
        {[
          { top: "22%", size: 26, dur: 30, delay: 0, drift: -10 },
          { top: "28%", size: 18, dur: 38, delay: 6, drift: 8 },
          { top: "18%", size: 20, dur: 34, delay: 12, drift: -6 },
        ].map((b, i) => (
          <motion.svg
            key={i}
            viewBox="0 0 64 24"
            width={b.size}
            height={b.size * 0.4}
            className="absolute text-black/45"
            style={{ top: b.top }}
            initial={{ x: "108vw" }}
            animate={{ x: "-12vw", y: [0, b.drift, 0] }}
            transition={{
              x: { duration: b.dur, repeat: Infinity, ease: "linear", delay: b.delay },
              y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
            }}
          >
            <path
              d="M2 20 Q16 4 32 14 Q48 4 62 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </motion.svg>
        ))}
      </div>

      {/* Cinematic tint layers for text legibility */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10 bg-gradient-to-b from-black/60 via-black/45 to-[#050505]"
      />
      {/* Left scrim — keeps the headline column dark and readable */}
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-black/90 via-black/55 to-transparent" />
      {/* Top scrim for the navbar */}
      <div className="absolute inset-x-0 top-0 h-40 z-10 bg-gradient-to-b from-black/70 to-transparent" />
      {/* Focused radial darkening behind the text block */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(120% 90% at 20% 55%, rgba(0,0,0,0.6), transparent 55%)",
        }}
      />
      {/* Extra mobile tint — portrait sky is bright, so darken for legibility */}
      <div className="absolute inset-0 z-10 pointer-events-none sm:hidden bg-gradient-to-t from-black/95 via-black/60 to-black/50" />

      {/* Architectural gold frame */}
      <div className="pointer-events-none absolute inset-6 md:inset-10 border border-[#d4af37]/20 z-20" />
      <div className="pointer-events-none absolute top-6 left-6 md:top-10 md:left-10 w-16 h-16 border-t-2 border-l-2 border-[#d4af37]/60 z-20" />
      <div className="pointer-events-none absolute bottom-6 right-6 md:bottom-10 md:right-10 w-16 h-16 border-b-2 border-r-2 border-[#d4af37]/60 z-20" />

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-30 mx-auto max-w-[1400px] px-8 md:px-16 min-h-screen flex flex-col justify-center"
      >
        {/* Faint gold glow behind the headline */}
        <motion.div
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="pointer-events-none absolute left-4 md:left-10 top-[38%] w-[520px] h-[420px] max-w-[80vw] rounded-full blur-[110px] bg-[#d4af37]/12"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="w-12 h-px bg-[#d4af37]" />
          <span className="text-[11px] md:text-xs tracking-luxe uppercase text-[#d4af37]">
            Kasauli · Himalayan Foothills
          </span>
        </motion.div>

        <h1 className="font-serif-lux leading-[1.08] text-[#f5f5f0] [text-shadow:0_2px_24px_rgba(0,0,0,0.55)]">
          <FadeLines
            lines={["Conscious luxury,"]}
            lineClass="text-2xl sm:text-3xl lg:text-4xl font-light italic text-[#e8cf7a]"
            delay={0.5}
          />
          <FadeLines
            lines={["Motiaz Sarrgosa"]}
            lineClass="text-5xl sm:text-6xl lg:text-7xl tracking-[0.02em] gold-gradient-text font-medium mt-2 pb-[0.12em]"
            delay={0.7}
          />
          <FadeLines
            lines={["above the clouds."]}
            lineClass="text-2xl sm:text-3xl lg:text-4xl font-light text-[#f5f5f0] mt-2"
            delay={0.95}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.3 }}
          className="mt-8 max-w-xl text-[#dedbd2] text-base lg:text-lg font-light leading-relaxed [text-shadow:0_1px_16px_rgba(0,0,0,0.6)]"
        >
          An exclusive 8-acre gated community of hill-view residences, where
          alpine architecture meets pine-fresh air — crafted for people who
          live consciously, and joyfully.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.5 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-enquire-btn"
            onClick={go}
            className="btn-gold rounded-full px-9 py-4 text-xs tracking-[0.18em] uppercase font-semibold"
          >
            Request the Private Brochure
          </button>
          <button
            data-testid="hero-explore-btn"
            onClick={() => scrollToSection("#manifesto")}
            className="btn-ghost rounded-full px-9 py-4 text-xs tracking-[0.18em] uppercase"
          >
            Explore the Estate
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] tracking-luxe uppercase text-[#d4af37]/70">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[#d4af37] to-transparent"
        />
      </motion.div>
    </section>
  );
}
